import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "../../config/config";
import { api_buildings_routes } from "../../config/apiRoutes";
import { Building, BuildingsState } from "../../types/types";

const initialState: BuildingsState = {
  buildings: [],
  loading: false,
  error: null,
};

export const fetchBuildings = createAsyncThunk<Building[], { query?: string }>(
  "buildings/fetchBuildings",
  async ({ query }, thunkAPI) => {
    try {
      const url = query
        ? `${api_buildings_routes.getAll_and_create}?query=${query}`
        : api_buildings_routes.getAll_and_create;

      const response = await API.GET(url);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.message || "Failed to fetch buildings"
      );
    }
  }
);

export const createBuilding = createAsyncThunk<any, any>(
  "buildings/createBuilding",
  async (newBuilding, { rejectWithValue }) => {
    try {
      const response = await API.POST(
        api_buildings_routes.getAll_and_create,
        newBuilding
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create building");
    }
  }
);

export const updateBuilding = createAsyncThunk<
  any,
  { id: string; updatedBuilding: any }
>(
  "buildings/updateBuilding",
  async ({ id, updatedBuilding }, { rejectWithValue }) => {
    try {
      const response = await API.PUT(
        `${api_buildings_routes.getAll_and_create}/${id}`,
        updatedBuilding
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update building");
    }
  }
);
export const toggleBuildingStatus = createAsyncThunk<
  any,
  { buildingId: any; isActive: boolean; callBack: any }
>(
  "buildings/toggleBuildingStatus",
  async ({ buildingId, isActive, callBack }, { rejectWithValue }) => {
    try {
      const response = await API.PUT(
        `${api_buildings_routes.getAll_and_create}/${buildingId}/toggle-status`,
        { isActive }
      );
      if (callBack) callBack(response.data);
      return response.data;
    } catch (error: any) {
      if (callBack) callBack(error.response);
      return rejectWithValue(
        error.message || "Failed to toggle building status"
      );
    }
  }
);

const buildingsSlice = createSlice({
  name: "buildings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuildings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuildings.fulfilled, (state, action) => {
        state.loading = false;
        state.buildings = action.payload;
      })
      .addCase(fetchBuildings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createBuilding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBuilding.fulfilled, (state, action) => {
        state.loading = false;
        state.buildings.push(action.payload);
      })
      .addCase(createBuilding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBuilding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBuilding.fulfilled, (state, action) => {
        state.loading = false;
        const updatedBuilding = action.payload;
        const index = state.buildings.findIndex(
          (b) => b._id === updatedBuilding._id
        );
        if (index !== -1) {
          state.buildings[index] = updatedBuilding;
        }
      })
      .addCase(updateBuilding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(toggleBuildingStatus.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      });
  },
});

export default buildingsSlice.reducer;
